/**
 * Pure-TS geometry helpers (002) — a deterministic reference used for unit tests
 * and as a lightweight client-side check.
 *
 * The authoritative validation runs in genz-engine (Rust, native + WASM). This TS
 * version is a faithful, deterministic stand-in so unit tests can assert geometry
 * behavior without the WASM build.
 */
import type { GeometryError, Mask, Point, Polygon, ValidationResult } from "./types.js";

export function signedArea(poly: Polygon): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

export function polygonArea(poly: Polygon): number {
  return Math.abs(signedArea(poly));
}

function segmentsIntersect(a: Point, b: Point, c: Point, d: Point): boolean {
  const o = (p: Point, q: Point, r: Point) =>
    Math.sign((q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y));
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  return o1 !== o2 && o3 !== o4;
}

/** True when the polygon has any non-adjacent edge crossing (self-intersecting). */
export function isSelfIntersecting(poly: Polygon): boolean {
  const n = poly.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // skip adjacent / shared-vertex edges
      if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1)) continue;
      const c = poly[j];
      const d = poly[(j + 1) % n];
      if (segmentsIntersect(a, b, c, d)) return true;
    }
  }
  return false;
}

/** Every coordinate is a real number. WS-ERR F-15: a NaN survives every comparison below as
 *  `false`, so without this it read as "out of bounds" and a CLAMP was attempted —
 *  `Math.max(0, NaN)` is NaN, so the repair changed nothing and reported success. */
export function isFinitePolygon(poly: Polygon): boolean {
  return poly.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}

export function withinBounds(poly: Polygon, min = 0, max = 1): boolean {
  return isFinitePolygon(poly) && poly.every((p) => p.x >= min && p.x <= max && p.y >= min && p.y <= max);
}

export function pointInPolygon(pt: Point, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const pi = poly[i];
    const pj = poly[j];
    const intersect =
      pi.y > pt.y !== pj.y > pt.y &&
      pt.x < ((pj.x - pi.x) * (pt.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Whether `cutout` overlaps `printArea` — mirrors the engine's `overlaps`: any cutout
 *  vertex inside the print area, any print-area vertex inside the cutout, or the cutout's
 *  centroid inside the print area. A cutout that overlaps subtracts SOMETHING. */
function overlaps(printArea: Polygon, cutout: Polygon): boolean {
  if (cutout.some((pt) => pointInPolygon(pt, printArea))) return true;
  if (printArea.some((pt) => pointInPolygon(pt, cutout))) return true;
  if (cutout.length > 0) {
    const cx = (Math.min(...cutout.map((p) => p.x)) + Math.max(...cutout.map((p) => p.x))) / 2;
    const cy = (Math.min(...cutout.map((p) => p.y)) + Math.max(...cutout.map((p) => p.y))) / 2;
    if (pointInPolygon({ x: cx, y: cy }, printArea)) return true;
  }
  return false;
}

/** Whether every cutout subtracts something from the print area. A cutout that lies
 *  ENTIRELY outside the print area subtracts nothing → `cutout_not_subtracted`. A cutout
 *  inside or straddling the edge is valid (matches the authoritative engine + server). */
function cutoutsSubtracted(printArea: Polygon, cutouts: Mask[]): boolean {
  if (cutouts.length === 0) return true;
  return cutouts.every((c) => overlaps(printArea, c.polygon));
}

export function validate(printArea: Polygon, cutouts: Mask[]): ValidationResult {
  const errors: GeometryError[] = [];
  if (polygonArea(printArea) <= 1e-6 || !isFinitePolygon(printArea)) errors.push("empty_area");
  if (isSelfIntersecting(printArea)) errors.push("self_intersect");
  if (!withinBounds(printArea)) errors.push("out_of_bounds");
  for (const c of cutouts) {
    // WS-ERR F-16 — the ZERO-AREA rule applies to every mask, matching
    // `cloud/app/catalog/geometry.py:89-95`. The engine applies it to the print area only
    // (`geometry/validate.rs:54`), so a cutout collapsed to a line — what a double-click on a
    // 3-point cutout leaves — passed the editor, passed Publish's local guard, and was rolled
    // back by the SERVER together with every sibling view. Where the two authorities differ,
    // the client takes the stricter: refusing locally costs a message, and the alternative
    // costs the whole submit.
    if (polygonArea(c.polygon) <= 1e-9 && !errors.includes("empty_area")) {
      errors.push("empty_area");
    }
    if (isSelfIntersecting(c.polygon) && !errors.includes("self_intersect")) {
      errors.push("self_intersect");
    }
    if (!withinBounds(c.polygon) && !errors.includes("out_of_bounds")) {
      errors.push("out_of_bounds");
    }
  }
  if (!cutoutsSubtracted(printArea, cutouts)) errors.push("cutout_not_subtracted");
  return { valid: errors.length === 0, errors };
}

/**
 * The OUTER ring of the print area minus its cutouts — a copy of the print area.
 *
 * The twin of `geometry::effective_print_area` (Rust), and it must answer the same thing. It
 * used to nudge any print-area vertex that fell inside a cutout toward the centroid, "so the
 * overlay visibly differs" — an answer the engine never gave, and a visibly WRONG outline
 * whenever it fired (WS-DET F-7). A `Polygon` is one ring and cannot carry a hole: the renderer
 * subtracts per-pixel, and the editor draws the holes itself (`MaskCanvas.tsx`).
 */
export function effectivePrintArea(printArea: Polygon, _cutouts: Mask[]): Polygon {
  return printArea.map((p) => ({ ...p }));
}

/** Ramer–Douglas–Peucker polyline simplification (closed polygon). */
export function simplify(poly: Polygon, epsilon: number): Polygon {
  if (poly.length <= 3) return poly.map((p) => ({ ...p }));
  const perpDist = (p: Point, a: Point, b: Point): number => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1e-9;
    return Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / len;
  };
  const rdp = (pts: Point[]): Point[] => {
    if (pts.length < 3) return pts;
    let maxD = 0;
    let idx = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      const d = perpDist(pts[i], pts[0], pts[pts.length - 1]);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (maxD > epsilon) {
      const left = rdp(pts.slice(0, idx + 1));
      const right = rdp(pts.slice(idx));
      return [...left.slice(0, -1), ...right];
    }
    return [pts[0], pts[pts.length - 1]];
  };
  return rdp(poly.map((p) => ({ ...p })));
}

// ---------------------------------------------------------------------------
// F-5 (a) — REPAIR. The fallback that keeps invalid geometry off the wire.
// ---------------------------------------------------------------------------

/**
 * Clip `subject` against the CONVEX polygon `clip` (Sutherland–Hodgman).
 *
 * The print area is a quad in every flow that reaches here (auto-detect emits one, the
 * manual seed is one, and the editor only moves its four vertices), so convexity holds.
 * Returns fewer than 3 points when nothing survives — that is the caller's "drop it" signal.
 * Winding is normalised to CCW first so a clockwise clip polygon does not invert the test.
 */
export function clipToPolygon(subject: Polygon, clip: Polygon): Polygon {
  if (subject.length < 3 || clip.length < 3) return [];
  const c = signedArea(clip) < 0 ? [...clip].reverse() : clip;
  const cross = (a: Point, b: Point, p: Point) => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  const intersect = (p: Point, q: Point, a: Point, b: Point): Point => {
    const d1 = cross(a, b, p);
    const d2 = cross(a, b, q);
    const t = d1 - d2 === 0 ? 0 : d1 / (d1 - d2);
    return { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t };
  };
  let out: Polygon = subject.map((p) => ({ ...p }));
  for (let i = 0; i < c.length && out.length > 0; i++) {
    const a = c[i];
    const b = c[(i + 1) % c.length];
    const input = out;
    out = [];
    for (let j = 0; j < input.length; j++) {
      const cur = input[j];
      const prev = input[(j + input.length - 1) % input.length];
      const curIn = cross(a, b, cur) >= -1e-12;
      const prevIn = cross(a, b, prev) >= -1e-12;
      if (curIn) {
        if (!prevIn) out.push(intersect(prev, cur, a, b));
        out.push({ ...cur });
      } else if (prevIn) {
        out.push(intersect(prev, cur, a, b));
      }
    }
  }
  return out;
}

/** Every vertex pulled back into the 0..1 image box. */
function clampToUnit(poly: Polygon): Polygon {
  return poly.map((p) => ({
    x: Math.min(1, Math.max(0, p.x)),
    y: Math.min(1, Math.max(0, p.y)),
  }));
}

/** What `repairGeometry` did, so the caller can SAY it instead of changing geometry silently. */
export interface GeometryRepair {
  printArea: Polygon;
  cutouts: Mask[];
  /** Cutouts removed because they subtract nothing from the print area. */
  dropped: number;
  /** Cutouts clipped back inside the print area. */
  clipped: number;
  /** WS-ERR F-14 — the INPUT indices of the cutouts that were dropped, and of those that were
   *  merely clipped. The UI's "Remove this cutout" may only ever offer to delete one of the
   *  former: a cutout that spilled over the edge of the image needs TRIMMING, and offering to
   *  delete it invites the operator to throw away a camera hole they wanted, in one click,
   *  from the banner that is supposed to be the safe way out. */
  droppedIndices: number[];
  clippedIndices: number[];
  /** The print area was pulled back inside the image. */
  clampedPrintArea: boolean;
  /** Anything at all changed. */
  changed: boolean;
  /** Errors a repair CANNOT fix — the print area itself is empty or crossed. The caller must
   *  show these and let the operator redraw; inventing a print area would be a guess. */
  unfixable: GeometryError[];
}

/**
 * Make `printArea` + `cutouts` valid BY CONSTRUCTION, or say what is left.
 *
 * F-5: "Auto-detect (on-device)" on a magenta case photo emits a camera cutout that sits
 * entirely ABOVE the print area (it shares only the print area's top edge). The engine and
 * the cloud both call that `cutout_not_subtracted` and refuse the Publish, and the only
 * signal was an "Invalid" line folded inside the "Print area & cutouts" rail section — so
 * the first thing the operator saw was a failed Publish. This runs right after detection.
 *
 * Valid geometry comes back UNTOUCHED (`changed: false`): a camera bump straddling the
 * print-area edge subtracts something and is exactly what the engine wants. Only the three
 * repairable faults are acted on, and each one is counted so the UI can name it.
 */
export function repairGeometry(printArea: Polygon, cutouts: Mask[]): GeometryRepair {
  const before = validate(printArea, cutouts);
  const unchanged: GeometryRepair = {
    printArea, cutouts, dropped: 0, clipped: 0, droppedIndices: [], clippedIndices: [],
    clampedPrintArea: false, changed: false, unfixable: [],
  };
  if (before.valid) return unchanged;

  // The print area itself: out-of-bounds is repairable (clamp); empty or self-crossing is not
  // — there is nothing to derive a correct polygon FROM, and a guess would be worse than a
  // message (audit class 7: a fallback may degrade a read, never invent a write).
  const unfixable: GeometryError[] = [];
  // F-15: a non-finite coordinate cannot be clamped back into range — `Math.max(0, NaN)` is
  // NaN — so it is REPORTED, never "repaired". This guard is first because every measurement
  // below (area, self-intersection, bounds) answers nonsense for a NaN.
  if (!isFinitePolygon(printArea)) unfixable.push("out_of_bounds");
  if (polygonArea(printArea) <= 1e-6) unfixable.push("empty_area");
  if (isSelfIntersecting(printArea)) unfixable.push("self_intersect");
  if (unfixable.length > 0) return { ...unchanged, unfixable };

  // Clamping is only a repair while the polygon SURVIVES it. A print area whose vertices all
  // sit outside the same edge collapses to a point under a clamp, which would turn one
  // reported error into a silently degenerate print area — a worse lie than the error.
  const clamped = withinBounds(printArea) ? null : clampToUnit(printArea);
  if (clamped && polygonArea(clamped) <= 1e-6) {
    return { ...unchanged, unfixable: ["out_of_bounds"] };
  }
  const clampedPrintArea = clamped !== null;
  const nextPrintArea = clamped ?? printArea;

  const droppedIndices: number[] = [];
  const clippedIndices: number[] = [];
  const nextCutouts: Mask[] = [];
  for (const [index, c] of cutouts.entries()) {
    const healthy =
      overlaps(nextPrintArea, c.polygon) &&
      withinBounds(c.polygon) &&
      !isSelfIntersecting(c.polygon) &&
      polygonArea(c.polygon) > 1e-9;
    if (healthy) {
      nextCutouts.push(c);
      continue;
    }
    // F-15: garbage in, nothing out. A cutout with a non-finite coordinate has no shape to
    // clip — dropping it is the only answer that leaves the result valid.
    if (!isFinitePolygon(c.polygon)) {
      droppedIndices.push(index);
      continue;
    }
    // Clip it back inside the print area. A cutout that lies entirely outside (the F-5 repro)
    // clips to nothing — or to a zero-area sliver along the shared edge — and is dropped.
    // F-16: a cutout that was ALREADY zero-area clips to nothing too, which is the drop the
    // cloud's stricter rule needs.
    const poly = clipToPolygon(c.polygon, nextPrintArea);
    if (poly.length < 3 || polygonArea(poly) <= 1e-9 || isSelfIntersecting(poly)) {
      droppedIndices.push(index);
      continue;
    }
    nextCutouts.push({ ...c, polygon: poly });
    clippedIndices.push(index);
  }

  return {
    printArea: nextPrintArea,
    cutouts: nextCutouts,
    dropped: droppedIndices.length,
    clipped: clippedIndices.length,
    droppedIndices,
    clippedIndices,
    clampedPrintArea,
    changed: clampedPrintArea || droppedIndices.length > 0 || clippedIndices.length > 0,
    unfixable: [],
  };
}

/** One plain sentence per engine error code — the operator's words, not the engine's.
 *  `Invalid: cutout_not_subtracted` is a log line, and it was the only thing on screen. */
const PROBLEM_SENTENCE: Record<string, string> = {
  cutout_not_subtracted: "A cutout sits outside the print area, so it cuts nothing out of it.",
  out_of_bounds: "A shape runs outside the image.",
  self_intersect: "A shape crosses over itself.",
  empty_area: "This view has no print area — draw or detect it.",
};

/**
 * The banner's body text for a `ValidationResult.errors` list.
 *
 * `empty_area` is the one code that means two different things, so it is the one that needs the
 * geometry to say which: the print area is missing, or a CUTOUT has collapsed to a line (F-16's
 * rule, which the cloud has always applied to every mask). "This view has no print area" in
 * front of an operator who is looking at their print area is a message that cannot be acted on.
 * Same for a print area whose coordinates are not finite — "draw or detect it" is not the fix.
 *
 * Never empty: an unknown code still gets a sentence, because "" renders a banner with nothing
 * in it.
 */
export function geometryProblemMessage(
  errors: GeometryError[],
  geometry?: { printArea: Polygon; cutouts: Mask[] },
): string {
  const seen = [...new Set(errors)];
  const parts = seen.map((e) => {
    if (e === "empty_area" && geometry) {
      if (!isFinitePolygon(geometry.printArea)) {
        return "The print area's corners are not real numbers — redraw it.";
      }
      if (polygonArea(geometry.printArea) > 1e-6) {
        // The print area is fine, so the zero-area mask is one of the cutouts.
        return "A cutout has no area — it has collapsed to a line or a point.";
      }
    }
    return PROBLEM_SENTENCE[e] ?? `The geometry is invalid (${e}).`;
  });
  if (parts.length === 0) return "The geometry is invalid.";
  return parts.join(" ");
}
